/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    isVariantTrulyAvailable(variant) {
      if (!variant || !variant.available) return false;
      
      const formattedData = this.data.formatted[variant.id];
      if (!formattedData) return false;
      
      if (formattedData.inventoryStatus === 'none') return false;
      
      if (formattedData.inventoryManagement && formattedData.inventoryManagement !== '') {
        const inventory = parseInt(formattedData.inventory) || 0;
        return inventory > 0;
      }
      
      return true;
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantTrulyAvailable = this.isVariantTrulyAvailable(this.variant);
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.textContent = variantTrulyAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available, hasStock = true) {
      const el = optionEl;
      const isTrulyAvailable = exists && available && hasStock;
      const unavailableText = exists ? (hasStock ? theme.strings.noStock : theme.strings.noStock) : theme.strings.noVariant;
      
      el.classList.toggle('is-unavailable', !isTrulyAvailable);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = isTrulyAvailable;
        }

        if (!isTrulyAvailable) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!isTrulyAvailable) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option?.toLowerCase() === currVariant.options[optionIndex]?.toLowerCase() && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value?.toLowerCase() === variant.options[selectorIndex]?.toLowerCase();
                }
                return opt.value?.toLowerCase() === variant.options[selectorIndex]?.toLowerCase();
              });

              if (optionEl) {
                const isTrulyAvailable = this.isVariantTrulyAvailable(variant);
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available, isTrulyAvailable);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            let variantsExist = false;
            let variantsAvailable = false;
            let variantsHaveStock = false;
            
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i]?.toLowerCase() === this.selectedOptions[i]?.toLowerCase() || 
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex]?.toLowerCase() === optionValue?.toLowerCase() && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                  if (this.isVariantTrulyAvailable(v)) {
                    variantsHaveStock = true;
                  }
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable, variantsHaveStock);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventoryStatus } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventoryStatus === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;
      const hasGroups = selector.querySelector('.color-group');
      if (hasGroups) {
        const colorGroups = selector.querySelectorAll('.color-group');
        colorGroups.forEach(group => {
          const colorText = group.querySelector('.js-color-text');
          if (colorText) colorText.classList.remove('selected');
          if (colorText) colorText.textContent = '';
        });
        const colorGroup = evt.target.closest('.color-group');
        if (!colorGroup) return;
        const colorText = colorGroup.querySelector('.js-color-text');
        if (!colorText) return;
        const label = evt.target.nextElementSibling;
        if (!label) return;
        const valueSpan = label.querySelector('.js-value');
        if (!valueSpan) return;
        colorText.textContent = ': ' + valueSpan.textContent;
        colorText.classList.add('selected');
        return;
      }
      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;
      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.isVariantTrulyAvailable(this.variant)) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) {
        console.warn('Price element not found in the section');
        return;
      }

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      let currentFormContainer = this.closest('.product-form')

      if (variant) {
        const priceCurrentEl = currentFormContainer.querySelector('.price__current');
        const priceWasEl = currentFormContainer.querySelector('.price__was');
        const unitPriceEl = currentFormContainer.querySelector('.unit-price');

        if (priceCurrentEl) priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

        if (variant.unit_price_measurement && unitPriceEl) {
          const valueEl = unitPriceEl.querySelector('.unit-price__price');
          const unitEl = unitPriceEl.querySelector('.unit-price__unit');
          if (valueEl && unitEl) {
            const value = variant.unit_price_measurement.reference_value;
            const unit = variant.unit_price_measurement.reference_unit;

            valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
            unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
            unitPriceEl.hidden = false;
          }
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        const isTrulyAvailable = this.isVariantTrulyAvailable(variant);
        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !isTrulyAvailable && !this.preSelection);
      }

      const priceDefaultEl = this.price.querySelector('.price__default');
      const priceNoVariantEl = this.price.querySelector('.price__no-variant');
      const priceFromEl = this.price.querySelector('.price__from');

      if (priceDefaultEl) priceDefaultEl.hidden = !this.variant && !this.preSelection;
      if (priceNoVariantEl) priceNoVariantEl.hidden = this.variant || this.preSelection;
      if (priceFromEl) priceFromEl.hidden = !this.preSelection;
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        const lstBtnsCheckout = form.querySelectorAll('.button--checkout');
        input.value = this.variant ? this.variant.id : '';

        // Update selected variant on buy now buttons
        lstBtnsCheckout.forEach((btn) => {
          btn.href = `${location.origin}${window.theme.routes.cart}/${this.variant ? this.variant.id : ''}:1?checkout`;
        });

        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.updateStickyPdp();
    }
    
    updateStickyPdp() {
      const stickyPdp = document.getElementById('sticky-element');
      if (!stickyPdp) return; 
      const stickyPdpBtn = stickyPdp.querySelector('.add-to-cart-sticky');
      const buyNowBtn = stickyPdp.querySelector('.button--checkout');
      const btnBuyNowPdp = this.closest('.product-form').querySelector('.button--checkout');
      const btnAddToCartPdp = this.closest('.product-form').querySelector('.add-to-cart');

      console.log('stickyPdpBtn', btnAddToCartPdp.textContent);
    
      if (!this.isVariantTrulyAvailable(this.variant)) {
        stickyPdpBtn.textContent = 'Notify me';
        buyNowBtn.classList.add('hidden');
        stickyPdpBtn.dataset.behavior = 'notify-me';
        btnBuyNowPdp.classList.add('hidden');
      } else {
        setTimeout(() => {
          if(btnAddToCartPdp.textContent == 'Pre-order') {
            console.log('Pre-order');
            stickyPdpBtn.textContent = 'Pre-order';
          }else{
            console.log('Add to bag');
            stickyPdpBtn.textContent = 'Add to bag';
          }
        }, 1000);
        buyNowBtn.classList.remove('hidden');
        stickyPdpBtn.dataset.behavior = 'add-to-cart';
        btnBuyNowPdp.classList.remove('hidden');
      }
    
      if (this.variant && Array.isArray(this.variant.options)) {
        this.variant.options.forEach((optValue, index) => {
          if (optValue !== null) {
            const optionBlock = stickyPdp.querySelector(`.variant-option[data-option="${index + 1}"]`);
            if (optionBlock) {
              const p = optionBlock.querySelector('p');
              if (p) {
                p.textContent = optValue;
              }
            }
          }
        });
      }
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
